import { Particle } from '../ParticleLayer';

const CONFIG = {
    baseScale: 0.45, // Much more reasonable size
    unearthDuration: 350,
    impactDuration: 250,
    pulseDuration: 1200,
    dissipateDuration: 600,
    paths: {
        shield: '/assets/effects/ancient_shield/shield.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.shield);

// ==========================================
// PARTICLES
// ==========================================

class Shockwave {
    x: number; y: number;
    life: number = 0;
    maxLife: number = 400;
    color: string;

    constructor(x: number, y: number, isOpponent = false) {
        this.x = x;
        this.y = y + (isOpponent ? -40 : 40);
        this.color = '#c026d3';
    }

    update(dt: number) { this.life += dt; }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const radius = 20 + progress * 250;
        const alpha = Math.max(0, 1 - progress);
        const thickness = Math.max(1, 25 * (1 - progress));

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.ellipse(this.x, this.y, radius, radius * 0.3, 0, 0, Math.PI * 2);

        ctx.strokeStyle = this.color;
        ctx.lineWidth = thickness;
        ctx.shadowColor = '#f0abfc';
        ctx.shadowBlur = 15;
        ctx.stroke();

        // Inner white hot ring
        ctx.beginPath();
        ctx.ellipse(this.x, this.y, radius * 0.95, radius * 0.95 * 0.3, 0, 0, Math.PI * 2);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = thickness * 0.3;
        ctx.stroke();
        ctx.restore();
    }
}

class GroundDust {
    x: number; y: number;
    vx: number; vy: number;
    life: number = 0;
    maxLife: number;
    size: number;

    constructor(x: number, y: number, isOpponent = false) {
        const yDir = isOpponent ? -1 : 1;
        const angle = Math.random() * Math.PI * 2;
        const speed = 1 + Math.random() * 4;
        this.x = x + Math.cos(angle) * 20;
        this.y = y + 40 * yDir + Math.sin(angle) * 10;

        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * (speed * 0.3) - (1 + Math.random() * 2) * yDir;

        this.maxLife = 400 + Math.random() * 300;
        this.size = 10 + Math.random() * 25;
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.92;
        this.vy *= 0.92;
        this.size += dt * 0.05;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const alpha = Math.max(0, (1 - progress) * 0.4); // Subtle dust

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = '#1e293b'; // Dark slaty dust
        ctx.fill();
        ctx.restore();
    }
}

class AuraRing {
    x: number; y: number;
    life: number = 0;
    maxLife: number;
    baseRadius: number;
    speed: number;
    rotation: number;

    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.maxLife = 800 + Math.random() * 400;
        this.baseRadius = 60 + Math.random() * 40;
        this.speed = 0.5 + Math.random() * 1.0;
        this.rotation = Math.random() * Math.PI * 2;
    }

    update(dt: number) {
        this.life += dt;
        this.baseRadius += this.speed * dt * 0.05;
        this.rotation += dt * 0.001;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const alpha = Math.max(0, 1 - progress) * 0.5;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.beginPath();
        ctx.arc(0, 0, this.baseRadius, 0, Math.PI * 2);

        // Dashed magical ring
        ctx.setLineDash([15, 20, 5, 20]);
        ctx.strokeStyle = '#c026d3';
        ctx.lineWidth = 10 * (1 - progress);
        ctx.shadowColor = '#f0abfc';
        ctx.shadowBlur = 10;
        ctx.stroke();

        ctx.restore();
    }
}

class CrystalSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number = 0;
    maxLife: number;
    size: number;
    color: string;

    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 40;
        this.y = y + (Math.random() - 0.5) * 80;

        this.vx = (Math.random() - 0.5) * 0.8;
        this.vy = -1 - Math.random() * 3;

        this.maxLife = 500 + Math.random() * 400;
        this.size = 2 + Math.random() * 3;

        const colors = ['#f0abfc', '#e879f9', '#ffffff', '#22d3ee'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vy *= 0.98;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const alpha = Math.max(0, 1 - (this.life / this.maxLife));

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.restore();
    }
}

class ShatterShard {
    x: number; y: number;
    vx: number; vy: number;
    rotation: number;
    rotSpeed: number;
    life: number = 0;
    maxLife: number;
    size: number;
    isCrystal: boolean;

    constructor(x: number, y: number, isOpponent = false) {
        const yDir = isOpponent ? -1 : 1;
        this.x = x + (Math.random() - 0.5) * 60;
        this.y = y + (Math.random() - 0.5) * 100;

        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 8;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 4 * yDir; // Explode out and up/down

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.5;

        this.maxLife = 500 + Math.random() * 300;
        this.size = 4 + Math.random() * 12;
        this.isCrystal = Math.random() > 0.5;
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.95;
        this.vy += 0.3; // Gravity pulls them down
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const alpha = Math.max(0, 1 - progress);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.beginPath();
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size * 0.7, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(-this.size * 0.4, 0);
        ctx.closePath();

        if (this.isCrystal) {
            ctx.globalCompositeOperation = 'screen';
            ctx.fillStyle = progress < 0.2 ? '#ffffff' : '#e879f9';
            ctx.shadowColor = '#c026d3';
            ctx.shadowBlur = 10;
        } else {
            ctx.fillStyle = '#64748b'; // Metal shard
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 1;
            ctx.stroke();
        }

        ctx.fill();
        ctx.restore();
    }
}

export type AncientShieldEffect = {
    kind: 'ancientShield'
    t: number
    tx: number; ty: number;
    isOpponent?: boolean;
    data?: {
        phase: 'UNEARTH' | 'IMPACT' | 'PULSE' | 'DISSIPATE'
        rings: AuraRing[]
        sparks: CrystalSpark[]
        shards: ShatterShard[]
        dusts: GroundDust[]
        shockwaves: Shockwave[]
        domeHueOffset: number
        impactFired: boolean
        dissipateFired: boolean
    }
}

export const createAncientShieldEffect = (tx: number, ty: number, isOpponent = false): AncientShieldEffect => ({
    kind: 'ancientShield', t: 0, tx, ty, isOpponent
});

export const renderAncientShieldEffect = (
    eff: AncientShieldEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        eff.data = {
            phase: 'UNEARTH',
            rings: [], sparks: [], shards: [], dusts: [], shockwaves: [],
            domeHueOffset: 0,
            impactFired: false,
            dissipateFired: false
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tUnearth = CONFIG.unearthDuration;
    const tImpact = tUnearth + CONFIG.impactDuration;
    const tPulse = tImpact + CONFIG.pulseDuration;
    const tTotal = tPulse + CONFIG.dissipateDuration;

    // Easing Functions
    const easeOutCubic = (x: number): number => 1 - Math.pow(1 - x, 3);
    const easeInCubic = (x: number): number => x * x * x;
    const easeOutBack = (x: number): number => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    };

    if (eff.t < tUnearth) d.phase = 'UNEARTH';
    else if (eff.t < tImpact) d.phase = 'IMPACT';
    else if (eff.t < tPulse) d.phase = 'PULSE';
    else d.phase = 'DISSIPATE';

    ctx.save();

    let shieldAlpha = 1;
    let currentScale = CONFIG.baseScale;
    let shieldY = eff.ty;
    let visualShakeX = 0;
    let visualShakeY = 0;

    const yDir = eff.isOpponent ? -1 : 1;

    // --- PHASE LOGIC ---

    if (d.phase === 'UNEARTH') {
        const p = eff.t / tUnearth;

        shieldAlpha = Math.min(1, p * 3); // Quick fade in

        // Rise up fast, scale up large
        const targetY = eff.ty - 60 * yDir; // Slightly past final position
        const startY = eff.ty + 200 * yDir;
        shieldY = startY + (targetY - startY) * easeOutCubic(p);

        currentScale = CONFIG.baseScale * 1.5 * p; // Shoots up huge

        // Spawn dirt/dust exploding from ground
        if (Math.random() < 0.8) {
            d.dusts.push(new GroundDust(eff.tx, eff.ty, eff.isOpponent));
        }

    } else if (d.phase === 'IMPACT') {
        const p = (eff.t - tUnearth) / CONFIG.impactDuration;

        shieldAlpha = 1;

        // Slam down into place
        const startY = eff.ty - 60 * yDir;
        const targetY = eff.ty; // Locks into final Y
        shieldY = startY + (targetY - startY) * easeOutBack(p);

        // Scale slams from 1.5x down to 1.0x (baseScale)
        currentScale = CONFIG.baseScale * (1.5 - 0.5 * easeOutBack(p));

        // Screen Shake effect
        if (p < 0.5) {
            visualShakeX = (Math.random() - 0.5) * 15 * (1 - p * 2);
            visualShakeY = (Math.random() - 0.5) * 15 * (1 - p * 2);
        }

        // Spawn Shockwave on very first frame of impact
        if (!d.impactFired) {
            d.impactFired = true;
            d.shockwaves.push(new Shockwave(eff.tx, eff.ty, eff.isOpponent));
            d.shockwaves.push(new Shockwave(eff.tx, eff.ty, eff.isOpponent)); // Double thickness

            // Explosion of sparks outwards
            for (let i = 0; i < 20; i++) d.sparks.push(new CrystalSpark(eff.tx, eff.ty));
        }

    } else if (d.phase === 'PULSE') {
        shieldAlpha = 1;
        shieldY = eff.ty;
        currentScale = CONFIG.baseScale;

        // Gentle bobbing
        const pulseP = (eff.t - tImpact) / CONFIG.pulseDuration;
        shieldY += Math.sin(pulseP * Math.PI * 4) * 5;

        d.domeHueOffset += dt;

        if (Math.random() < 0.5) d.sparks.push(new CrystalSpark(eff.tx, eff.ty));
        if (eff.t % 400 < 20) d.rings.push(new AuraRing(eff.tx, eff.ty));

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tPulse) / CONFIG.dissipateDuration;

        // Implode scale slightly then vanish
        currentScale = CONFIG.baseScale * (1 - easeInCubic(p) * 0.5);
        shieldAlpha = 1 - easeInCubic(p); // Fade out

        // Start shattering instantly
        if (!d.dissipateFired) {
            d.dissipateFired = true;
            for (let i = 0; i < 40; i++) d.shards.push(new ShatterShard(eff.tx, eff.ty, eff.isOpponent));
            // Final gasp shockwave
            d.shockwaves.push(new Shockwave(eff.tx, eff.ty, eff.isOpponent));
        }
    }

    // --- RENDER ORDER ---

    // 1. Dust & Background Rings
    d.dusts = d.dusts.filter(s => s.life < s.maxLife);
    d.dusts.forEach(s => { s.update(dt); s.draw(ctx); });

    d.rings = d.rings.filter(r => r.life < r.maxLife);
    d.rings.forEach(r => { r.update(dt); r.draw(ctx); });

    d.shockwaves = d.shockwaves.filter(r => r.life < r.maxLife);
    d.shockwaves.forEach(r => { r.update(dt); r.draw(ctx); });

    // 2. Dome Energy Field (Active during Pulse Phase)
    if ((d.phase === 'PULSE' || d.phase === 'IMPACT') && shieldAlpha > 0) {
        ctx.save();
        const domeAlpha = d.phase === 'IMPACT' ? ((eff.t - tUnearth) / CONFIG.impactDuration) : shieldAlpha;
        const pulseGlow = Math.sin(d.domeHueOffset * 0.005) * 0.2 + 0.8; // 0.6 -> 1.0 glow pulse

        ctx.globalAlpha = domeAlpha * pulseGlow;
        ctx.globalCompositeOperation = 'screen';

        const grad = ctx.createRadialGradient(eff.tx, eff.ty, 0, eff.tx, eff.ty, 180 * CONFIG.baseScale * 2);
        grad.addColorStop(0, 'rgba(192, 38, 211, 0)');
        grad.addColorStop(0.5, 'rgba(162, 28, 175, 0.4)');
        grad.addColorStop(0.8, 'rgba(2, 132, 199, 0.7)'); // Cyan rim
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(eff.tx, eff.ty, 150 * currentScale * 2, 200 * currentScale * 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    // 3. Main Shield
    if (shieldAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = shieldAlpha;

        const localOffsetX = eff.isOpponent ? 0 : 200;
        const localOffsetY = eff.isOpponent ? 0 : 230;

        ctx.translate(eff.tx + visualShakeX + localOffsetX, shieldY + visualShakeY + localOffsetY);
        ctx.scale(currentScale, currentScale);

        if (!eff.isOpponent) {
            ctx.rotate(Math.PI);
        }

        const img = getSprite(CONFIG.paths.shield);
        if (img.complete && img.naturalWidth > 0) {
            // Intense drop shadow
            ctx.shadowColor = 'rgba(0,0,0,0.9)';
            ctx.shadowBlur = 30;
            ctx.shadowOffsetY = eff.isOpponent ? 20 : -20; // Correct shadow direction when upside down

            // Visual center offset since the shield design is very top-heavy
            const offsetY = eff.isOpponent ? 0 : 350;

            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2 + offsetY);
        }
        ctx.restore();
    }

    // 4. Foreground Sparks & Shards
    d.sparks = d.sparks.filter(s => s.life < s.maxLife);
    d.sparks.forEach(s => { s.update(dt); s.draw(ctx); });

    d.shards = d.shards.filter(s => s.life < s.maxLife);
    d.shards.forEach(s => { s.update(dt); s.draw(ctx); });

    ctx.restore();

    return eff.t < tTotal;
};
